import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader, LoadingScreen, EmptyState } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

function ProfileModal({ user, onClose, s }) {
  const color = user.admin_type === 'university' ? COLORS.purple : COLORS.green;
  const rows = [
    { label: 'Email',      value: user.email },
    { label: 'Type',       value: user.admin_type === 'university' ? 'University Admin' : 'Department Admin' },
    { label: 'Department', value: user.department || (user.admin_type === 'university' ? 'All Departments' : '—') },
    { label: 'Status',     value: user.is_active === false ? 'Inactive' : 'Active' },
    { label: 'Joined',     value: user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—' },
  ];
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}
      onClick={onClose}>
      <div style={{ ...s.card, width:'100%', maxWidth:420, padding:0, overflow:'hidden', animation:'scaleIn 0.2s ease-out' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding:'24px 24px 18px', background:`linear-gradient(135deg,${color}22,${color}08)`, borderBottom:`1px solid ${color}33`, position:'relative' }}>
          <button onClick={onClose} style={{ position:'absolute', top:12, right:12, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, color:'#fff', cursor:'pointer', width:28, height:28, fontSize:16, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:56, height:56, borderRadius:'50%', background:`linear-gradient(135deg,${color}55,${color}22)`, border:`2px solid ${color}66`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:900, color:'#fff', flexShrink:0 }}>
              {user.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <div style={{ fontSize:17, fontWeight:800, color:'#fff' }}>{user.name}</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', marginTop:2 }}>{user.email}</div>
              <div style={{ display:'flex', gap:6, marginTop:7, flexWrap:'wrap' }}>
                <span style={{ fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20, background:`${color}33`, color, border:`1px solid ${color}55` }}>
                  🛡️ ADMIN
                </span>
                <span style={{ fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20, background:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.7)', border:'1px solid rgba(255,255,255,0.2)' }}>
                  {(user.admin_type || '').toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div style={{ display:'flex', borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
          {[
            { label:'Verified',   value: user.verified_count   ?? 0, color: COLORS.blue  },
            { label:'Rejected',   value: user.rejected_count   ?? 0, color: '#f87171'    },
            { label:'Negligence', value: user.negligence_count ?? 0, color: COLORS.amber },
          ].map(st => (
            <div key={st.label} style={{ flex:1, padding:'12px 0', textAlign:'center', borderRight:'1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ fontSize:20, fontWeight:800, color:st.color }}>{st.value}</div>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginTop:2, textTransform:'uppercase', letterSpacing:0.5 }}>{st.label}</div>
            </div>
          ))}
        </div>
        <div style={{ padding:'16px 24px 22px' }}>
          {rows.map(({ label, value }) => (
            <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontSize:11, color:'rgba(255,255,255,0.4)', fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>{label}</span>
              <span style={{ fontSize:13, color:'#fff', fontWeight:600, maxWidth:220, textAlign:'right', wordBreak:'break-all' }}>{value}</span>
            </div>
          ))}
          {(user.negligence_count > 0) && (
            <div style={{ marginTop:12, padding:'7px 12px', borderRadius:8, background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.3)', fontSize:12, color:'#fca5a5', display:'flex', alignItems:'center', gap:8 }}>
              ⚠ {user.negligence_count} negligence flag{user.negligence_count !== 1 ? 's' : ''} recorded
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ManageAdminsInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchAdmins = async () => {
    try {
      const res = await api.get('/higher-authority/admins');
      setAdmins(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Deactivate this admin?')) return;
    setDeleting(id);
    try {
      await api.delete(`/higher-authority/delete-admin/${id}`);
      setAdmins(prev => prev.filter(a => a.id !== id));
    } catch (e2) {
      setError(e2.response?.data?.error || 'Failed to deactivate');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return <LoadingScreen />;

  const uniAdmins  = admins.filter(a => a.admin_type === 'university');
  const deptAdmins = admins.filter(a => a.admin_type === 'department');

  const AdminCard = ({ admin }) => (
    <div className="ur-card-hover" onClick={() => setSelected(admin)}
      style={{ ...s.card, display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', marginBottom:10, cursor:'pointer' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ width:38, height:38, borderRadius:'50%', background: admin.admin_type === 'university' ? 'rgba(99,102,241,0.3)' : 'rgba(16,185,129,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:'#fff', flexShrink:0 }}>
          {admin.name?.[0]?.toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:'#fff' }}>{admin.name}</div>
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.6)' }}>{admin.email}</div>
          {admin.department && <div style={{ fontSize:11, color:COLORS.cyan, marginTop:2 }}>{admin.department}</div>}
        </div>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, flexDirection:'column', alignItems:'flex-end' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20,
            background: admin.admin_type === 'university' ? 'rgba(99,102,241,0.25)' : 'rgba(16,185,129,0.25)',
            color: admin.admin_type === 'university' ? '#a5b4fc' : '#6ee7b7',
            border: `1px solid ${admin.admin_type === 'university' ? 'rgba(99,102,241,0.4)' : 'rgba(16,185,129,0.4)'}`,
          }}>
            {admin.admin_type === 'university' ? 'UNIVERSITY' : 'DEPARTMENT'}
          </span>
          <button className="ur-btn-hover"
            style={{ ...s.btnPrimary, fontSize:11, padding:'4px 10px', background:'rgba(239,68,68,0.3)', border:'1px solid rgba(239,68,68,0.5)', opacity: deleting === admin.id ? 0.6 : 1 }}
            onClick={e => handleDelete(admin.id, e)} disabled={deleting === admin.id}>
            {deleting === admin.id ? '...' : 'Deactivate'}
          </button>
        </div>
        {admin.negligence_count > 0 && (
          <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:20, background: admin.negligence_count >= 3 ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.2)', color: admin.negligence_count >= 3 ? '#fca5a5' : '#fcd34d', border:`1px solid ${admin.negligence_count >= 3 ? 'rgba(239,68,68,0.4)' : 'rgba(245,158,11,0.35)'}` }}>
            ⚠ {admin.negligence_count} negligence
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.bgGrid} /><div style={s.bgOrb1} /><div style={s.bgOrb2} /><div style={s.bgOrb3} />
      <div style={s.layout}>
        <Sidebar collapsed={!sidebarOpen} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition:'margin-left 0.3s ease' }}>
          <Topbar title="Manage Admins" subtitle={`${admins.length} active admins`}
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={<button className="ur-btn-hover" style={s.btnPrimary} onClick={() => navigate('/higher-authority/create-admin')}>+ Create Admin</button>}
          />
          <div style={s.content}>
            <PageHeader icon="👥" title="Manage Admins"
              stats={[{ label:'University', value:uniAdmins.length }, { label:'Department', value:deptAdmins.length }]}
            />
            {error && <div style={{ ...s.alertErr, marginBottom:16 }}>{error}</div>}
            {!admins.length ? <EmptyState text="No admins yet" sub="Create your first admin" /> : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:20 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:'#a5b4fc', marginBottom:12, textTransform:'uppercase', letterSpacing:0.8 }}>University Admins ({uniAdmins.length})</div>
                  {uniAdmins.length ? uniAdmins.map(a => <AdminCard key={a.id} admin={a} />) : <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', fontStyle:'italic' }}>None yet</div>}
                </div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:'#6ee7b7', marginBottom:12, textTransform:'uppercase', letterSpacing:0.8 }}>Department Admins ({deptAdmins.length})</div>
                  {deptAdmins.length ? deptAdmins.map(a => <AdminCard key={a.id} admin={a} />) : <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', fontStyle:'italic' }}>None yet</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {selected && <ProfileModal user={selected} onClose={() => setSelected(null)} s={s} />}
    </div>
  );
}

export default function ManageAdmins() {
  return <ThemeProvider><ManageAdminsInner /></ThemeProvider>;
}
